import { useEffect, useMemo, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from "react"
import { CheckCircle2, Save, Undo2 } from "lucide-react"

import { messageFor } from "../shared"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

// Spreadsheet building blocks shared by every panel-data screen (payroll, revenue,
// forecast): rows are subjects, columns are months, cells are typed into directly.

// ─── numbers ────────────────────────────────────────────────────────────────

/** Accepts Persian/Arabic digits and thousands separators; returns a plain decimal string. */
export function normalizeNumber(input: string) {
  return input
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/[٫]/g, ".")
    .replace(/[٬،,\s]/g, "")
    .trim()
}
export const isValidNumber = (value: string) => value === "" || /^\d+(\.\d{1,4})?$/.test(value)
export const displayNumber = (value: string) => (value === "" || !isValidNumber(value) ? value : Number(value).toLocaleString("fa-IR", { maximumFractionDigits: 4 }))
export const sumDisplay = (value: number) => value.toLocaleString("fa-IR", { maximumFractionDigits: 1 })
const MONTHS = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"]
/** «مهر ۰۴» — compact column header. */
export const monthLabel = (year: number, month: number) => `${MONTHS[month - 1]} ${String(year % 100).padStart(2, "0").replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]!)}`

// ─── look ───────────────────────────────────────────────────────────────────

/** Excel-like table: gridlines everywhere, grey headers, sticky first column. */
export const sheet = {
  wrap: "scroll-top overflow-x-auto rounded-md border border-neutral-300 bg-white dark:bg-background",
  table: "w-max min-w-full border-collapse text-xs md:text-sm",
  th: "border border-neutral-300 bg-neutral-100 px-2 py-1.5 text-center font-medium text-neutral-700 dark:bg-muted dark:text-foreground",
  corner: "sticky right-0 z-20 border border-neutral-300 bg-neutral-100 px-3 py-1.5 text-right font-medium dark:bg-muted",
  rowHead: "sticky right-0 z-10 border border-neutral-300 bg-white px-3 py-1 text-right font-normal dark:bg-background",
  td: "border border-neutral-300 p-0",
  total: "border border-neutral-300 bg-neutral-50 px-2 text-center tabular-nums font-medium dark:bg-muted/50",
  section: "border border-neutral-300 bg-neutral-200/70 px-3 py-1 text-right text-xs font-semibold text-neutral-700 dark:bg-muted dark:text-foreground",
}

export function SheetTable({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className={sheet.wrap}>
      <table className={sheet.table} aria-label={label}>{children}</table>
    </div>
  )
}

// ─── edit buffer ────────────────────────────────────────────────────────────

/** Tracks typed-but-unsaved cells against the saved values (keyed by any string). */
export function useEdits(original: Map<string, string> | undefined) {
  const base = useMemo(() => original ?? new Map<string, string>(), [original])
  const [edits, setEdits] = useState<Map<string, string>>(new Map())
  useEffect(() => setEdits(new Map()), [base])
  const apply = (current: Map<string, string>, key: string, value: string) => {
    const normalized = normalizeNumber(value)
    if (normalized === (base.get(key) ?? "")) current.delete(key)
    else current.set(key, normalized)
  }
  return {
    get: (key: string) => edits.get(key) ?? base.get(key) ?? "",
    isEdited: (key: string) => edits.has(key),
    set: (key: string, value: string) => setEdits((current) => { const next = new Map(current); apply(next, key, value); return next }),
    setMany: (entries: Array<[string, string]>) => setEdits((current) => { const next = new Map(current); for (const [key, value] of entries) apply(next, key, value); return next }),
    entries: () => [...edits.entries()],
    dirty: edits.size,
    invalid: [...edits.values()].filter((value) => !isValidNumber(value)).length,
    reset: () => setEdits(new Map()),
  }
}
export type Edits = ReturnType<typeof useEdits>

export function useUnsavedGuard(dirty: number) {
  useEffect(() => {
    if (!dirty) return
    const handler = (event: BeforeUnloadEvent) => { event.preventDefault() }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [dirty])
  return (action: () => void) => {
    if (!dirty || window.confirm("تغییرات ذخیره‌نشده از بین می‌رود. ادامه می‌دهید؟")) action()
  }
}

type SaveState = { isPending: boolean; isSuccess: boolean; error: unknown; data?: unknown; mutate: () => void; reset: () => void }

export function SaveBar({ edits, save }: { edits: Edits; save: SaveState }) {
  const [justSaved, setJustSaved] = useState(false)
  useEffect(() => {
    if (!save.isSuccess) return
    setJustSaved(true)
    const timer = setTimeout(() => setJustSaved(false), 4000)
    return () => clearTimeout(timer)
  }, [save.isSuccess, save.data])
  if (!edits.dirty && !save.error && !justSaved) return null
  return (
    <div className="sticky bottom-4 z-30 flex flex-wrap items-center gap-3 rounded-xl border bg-background/95 p-3 shadow-lg backdrop-blur" role="status" aria-live="polite">
      {edits.dirty ? (
        <>
          <span className="text-sm font-medium">{edits.dirty.toLocaleString("fa-IR")} خانه تغییر کرده و ذخیره نشده است</span>
          {edits.invalid > 0 && <span className="text-sm text-destructive">{edits.invalid.toLocaleString("fa-IR")} خانه عدد معتبر ندارد</span>}
          {save.error ? <span className="text-sm text-destructive">{messageFor(save.error)}</span> : null}
          <div className="ms-auto flex gap-2">
            <Button variant="ghost" onClick={() => { save.reset(); edits.reset() }}><Undo2 className="size-4" /> انصراف</Button>
            <Button disabled={edits.invalid > 0 || save.isPending} onClick={() => save.mutate()}><Save className="size-4" /> {save.isPending ? "در حال ذخیره…" : "ذخیره"}</Button>
          </div>
        </>
      ) : save.error ? <span className="text-sm text-destructive">{messageFor(save.error)}</span> : (
        <span className="flex items-center gap-2 text-sm text-success"><CheckCircle2 className="size-4" /> ذخیره شد.</span>
      )}
    </div>
  )
}

// ─── the cell ───────────────────────────────────────────────────────────────

/**
 * One spreadsheet cell. Arrow keys / Enter move between cells (left = next month in
 * RTL), Home/End jump to the row edges, Tab works as usual, and pasting a block
 * copied from Excel fills the cells below and beside. `hint` shows a carried default
 * (e.g. last month's salary) while empty, always with a dotted underline so it never
 * reads as a typed value. Locked (`disabled`) cells render as plain text instead of
 * greyed-out inputs: full-contrast, no Tab stop, no screen-reader digit confusion.
 */
export function NumberCell({ grid, row, col, value, hint, edited, disabled, label, title, tone, onChange, onPasteGrid }: {
  grid: string; row: number; col: number; value: string; hint?: string; edited?: boolean; disabled?: boolean; label: string; title?: string
  /** Row colour so actual and forecast rows are told apart at a glance. */
  tone?: "actual" | "forecast"
  onChange: (value: string) => void; onPasteGrid?: (matrix: string[][]) => void
}) {
  const [focused, setFocused] = useState(false)
  const invalid = !isValidNumber(normalizeNumber(value))

  if (disabled) {
    const typed = displayNumber(value)
    const shown = typed || (hint ? displayNumber(hint) : "")
    const isHint = !typed && !!hint
    return (
      <span
        title={title ?? label}
        className={cn(
          "block min-h-7 px-1.5 py-1 text-right tabular-nums",
          tone === "actual" && "bg-emerald-50 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-100",
          tone === "forecast" && "bg-sky-100 text-sky-950 dark:bg-sky-950/40 dark:text-sky-100",
          !tone && shown && "text-foreground",
          isHint && "text-neutral-600 underline decoration-dotted underline-offset-4",
          !shown && "text-neutral-500",
        )}
      >{shown || "—"}</span>
    )
  }

  // Rows/columns are numbered over input cells only, so the next cell is always row±1 / col±1.
  const move = (event: KeyboardEvent<HTMLInputElement>, dRow: number, dCol: number) => {
    event.preventDefault()
    for (let step = 1; step < 300; step += 1) {
      const element = document.querySelector<HTMLInputElement>(`input[data-grid="${grid}"][data-row="${row + dRow * step}"][data-col="${col + dCol * step}"]`)
      if (!element) return
      if (!element.disabled) { element.focus(); element.select(); return }
    }
  }
  const jumpEdge = (event: KeyboardEvent<HTMLInputElement>, first: boolean) => {
    event.preventDefault()
    const cells = [...document.querySelectorAll<HTMLInputElement>(`input[data-grid="${grid}"][data-row="${row}"]`)].filter((element) => !element.disabled)
    const target = first ? cells[0] : cells.at(-1)
    target?.focus(); target?.select()
  }
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "ArrowDown") move(event, event.shiftKey && event.key === "Enter" ? -1 : 1, 0)
    else if (event.key === "ArrowUp") move(event, -1, 0)
    else if (event.key === "ArrowLeft") move(event, 0, 1)
    else if (event.key === "ArrowRight") move(event, 0, -1)
    else if (event.key === "Home") jumpEdge(event, true)
    else if (event.key === "End") jumpEdge(event, false)
    else if (event.key === "Escape") (event.target as HTMLInputElement).blur()
  }
  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text")
    if (!onPasteGrid || !/[\t\n]/.test(text.trim())) return
    event.preventDefault()
    onPasteGrid(text.replace(/\r/g, "").replace(/\n$/, "").split("\n").map((line) => line.split("\t")))
  }
  const showHint = !focused && value === "" && hint

  return (
    <input
      type="text"
      inputMode="decimal"
      dir="ltr"
      data-grid={grid}
      data-row={row}
      data-col={col}
      aria-label={label}
      aria-invalid={invalid || undefined}
      title={title}
      value={focused ? value : showHint ? displayNumber(hint) : displayNumber(value)}
      onFocus={(event) => { setFocused(true); requestAnimationFrame(() => event.target.select()) }}
      onBlur={() => setFocused(false)}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      className={cn(
        "block h-7 w-full min-w-[5.5rem] border-0 bg-transparent px-1.5 text-right tabular-nums outline-none",
        "focus:relative focus:z-10 focus:bg-white focus:outline-2 focus:outline-offset-[-2px] focus:outline-success dark:focus:bg-background",
        tone === "actual" && "bg-emerald-50 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-100",
        tone === "forecast" && "bg-sky-100 text-sky-950 dark:bg-sky-950/40 dark:text-sky-100",
        showHint && "text-neutral-500 italic underline decoration-dotted underline-offset-4",
        edited && !invalid && "bg-amber-50 dark:bg-amber-950/30",
        invalid && "bg-destructive/10 text-destructive",
      )}
    />
  )
}
